import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Drawer, IconButton, Link, Skeleton, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Close, Undo } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { JOURNAL_SOURCE_HOME, JOURNAL_SOURCE_LABEL, JournalEntryDetail } from '../../../types/finance';
import { useTimezone } from '../../../hooks/useTimezone';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { useCurrencyFormat } from '../../../hooks/useCurrencyFormat';
import { InfoField, InfoGrid } from '../../common/SectionCard';
import ReasonDialog from '../../common/ReasonDialog';
import JournalLinesTable from '../ledger/JournalLinesTable';
import JournalStatusChip from './JournalStatusChip';

/**
 * One voucher in full: when, why, who, and its debit/credit lines. Linked
 * reversals open in place. Manual journals are reversed here; other
 * entries are reversed from the document that created them.
 */
const JournalEntryDrawer: React.FC<{ entryId: string | null; onClose: () => void; onChanged: () => void }> = ({
  entryId, onClose, onChanged,
}) => {
  const { formatDate, formatDateTime } = useTimezone();
  const { canWrite } = useModulePermissions();
  const money = useCurrencyFormat();
  const { enqueueSnackbar } = useSnackbar();
  const [currentId, setCurrentId] = useState<string | null>(entryId);
  const [entry, setEntry] = useState<JournalEntryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reversing, setReversing] = useState(false);

  useEffect(() => { setCurrentId(entryId); }, [entryId]);

  const load = useCallback(async (id: string) => {
    setEntry(null);
    setError(null);
    try {
      setEntry(await financeService.getJournalEntry(id));
    } catch (e: any) {
      setError(e?.message || 'Could not load this entry.');
    }
  }, []);

  useEffect(() => { if (currentId) load(currentId); }, [currentId, load]);

  const canReverse = !!entry && canWrite('finance_journal') && entry.sourceType === 'MANUAL_JOURNAL'
    && entry.status === 'POSTED' && !entry.reversesEntryId;

  const link = (id: string | null, no: string | null) =>
    id && <Link component="button" variant="body2" onClick={() => setCurrentId(id)} sx={{ fontFamily: 'monospace' }}>{no}</Link>;

  return (
    <Drawer
      anchor="right"
      open={!!entryId}
      onClose={onClose}
      // Lift above the fixed app bar, like the other record drawers.
      slotProps={{ root: { style: { zIndex: 1250 } }, paper: { style: { zIndex: 1250 }, sx: { width: { xs: '100%', sm: 560 } } } }}
    >
      <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>Journal entry</Typography>
          <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
        </Stack>
        {entry ? (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'monospace' }}>{entry.entryNo}</Typography>
            <JournalStatusChip entry={entry} />
          </Stack>
        ) : <Skeleton width={220} height={32} />}
        {entry && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{entry.memo}</Typography>}
      </Box>

      <Box sx={{ p: 2.5, overflowY: 'auto' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {!entry && !error && <Stack spacing={1.5}>{[0, 1, 2, 3].map(i => <Skeleton key={i} height={28} />)}</Stack>}
        {entry && (
          <Stack spacing={2.5}>
            {entry.status === 'REVERSED' && (
              <Alert severity="warning" variant="outlined">
                Reversed by {link(entry.reversedByEntryId, entry.reversedByEntryNo)} — {entry.reversalReason}
              </Alert>
            )}
            {entry.reversesEntryId && (
              <Alert severity="info" variant="outlined">This entry reverses {link(entry.reversesEntryId, entry.reversesEntryNo)}.</Alert>
            )}

            <InfoGrid>
              <InfoField label="Date" value={formatDate(entry.entryDate)} />
              <InfoField label="Amount" value={money(entry.totalPaisa / 100)} />
              <InfoField label="Source" value={JOURNAL_SOURCE_LABEL[entry.sourceType] || entry.sourceType} />
              <InfoField label="Recorded" value={`${entry.createdByName ?? 'Unknown'} · ${formatDateTime(entry.createdAt)}`} />
              {entry.approvedByName && <InfoField label="Approved by" value={entry.approvedByName} />}
            </InfoGrid>

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <JournalLinesTable lines={entry.lines} />
            </Box>

            {!entry.reversesEntryId && JOURNAL_SOURCE_HOME[entry.sourceType] && (
              <Typography variant="caption" color="text.secondary">
                Posted automatically. To undo it, reverse the record in{' '}
                <Link component={RouterLink} to={JOURNAL_SOURCE_HOME[entry.sourceType].path} onClick={onClose} underline="hover">
                  {JOURNAL_SOURCE_HOME[entry.sourceType].label}
                </Link>.
              </Typography>
            )}

            {canReverse && (
              <Box>
                <Button variant="outlined" color="error" startIcon={<Undo />} onClick={() => setReversing(true)}>Reverse entry</Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                  Posts the mirror image today. This entry stays on record, marked reversed.
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {entry && (
        <ReasonDialog
          open={reversing}
          onClose={() => setReversing(false)}
          title={`Reverse ${entry.entryNo}?`}
          consequence="A mirror entry dated today cancels its effect on every account. Both stay in the journal."
          facts={[{ label: 'Amount', value: money(entry.totalPaisa / 100) }, { label: 'Dated', value: formatDate(entry.entryDate) }]}
          onConfirm={async reason => {
            const res = await financeService.reverseJournalEntry(entry._id, reason);
            enqueueSnackbar(`Reversed — ${res.entryNo}`, { variant: 'success' });
            onChanged();
            await load(entry._id);
          }}
        />
      )}
    </Drawer>
  );
};

export default JournalEntryDrawer;
